import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, addDoc, serverTimestamp, doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import InviteFriendDailyVibeFlow from './InviteFriendDailyVibeFlow';
import theFoxMini from '../../TheFoxMini.png';
import orheartImage from '../../orheart.webp';
import friendsImage from '../../friends.webp';
import { seedFromString } from '../../utils/seedFromString';
import { getPrimarySavedPoster } from '../../utils/primaryCollage';
import { posterFindLuvIconFlags } from '../../utils/posterFindLuv';
import { showFriendFoxMaskPlaceholder } from '../../utils/friendCollageCutoutPlaceholder';
import FriendFoxMaskPlaceholderCutout from '../collage/FriendFoxMaskPlaceholderCutout';
import SpinWheelInline from '../SpinWheel';
import { rewardTilePersistenceKey } from '../../utils/rewardTilePersistence';
import { formatLookingForGendersShort } from '../feed/CollageDetailsLuvLegend';
import HeroMatrixAura from '../home/HeroMatrixAura';
import PaidForDateExplainer from '../home/PaidForDateExplainer';
import CollageFriendModal from './CollageFriendModal';
import FoxMaskCutoutLoader from '../collage/FoxMaskCutoutLoader';
import { auraRgbToStyleVars, parseAuraRgb } from '../../utils/auraColor';
import { displayVibeAccuracyPercent } from '../../utils/vibeAccuracy';
import {
  resolveVibeCheckStreakDisplay,
  VIBE_CHECK_STREAK_MAX,
  loadDailyVibeChecksForMonth,
  loadDailyVibeCheckForPeriod,
  getDailyVibePeriodKey,
  hasDailyVibeCheckForPeriod,
  subscribeToAcceptedDailyVibeShares,
  subscribeToDailyVibeCheckForPeriod,
} from '../../utils/dailyVibeFirestore';
import { referFriendEmailValidationError } from '../../utils/berkeleyEmailAccess';
import PendingEduEmailField from '../auth/PendingEduEmailField';
import '../../styles/Messaging.css';
import './UserMenuCollagesSection.css';

const SELF_GENDER_LABEL = { male: 'M', female: 'F', nb: 'NB' };

function isShare50ForFriendReward(reward) {
  if (!reward || typeof reward !== 'object') return false;
  const label = reward.spinPrizeLabel != null ? String(reward.spinPrizeLabel) : '';
  const title = reward.title != null ? String(reward.title) : '';
  return label === '$50 for your friend' || title === '$50 for your friend';
}

function notificationEmailValidationError(raw) {
  const email = String(raw || '').trim().toLowerCase();
  if (!email) return 'Enter an email.';
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return 'Enter a valid email.';
  return '';
}

/** ARE YOU (self) — single value for primary collage chip */
function formatSelfGenderShort(poster) {
  const g = poster?.selfGender;
  if (!g || !SELF_GENDER_LABEL[g]) return null;
  return SELF_GENDER_LABEL[g];
}

function UserMenuMainCollagePreview({ poster, onEditMainCollage, onShareMainCollage, onTryNewMask }) {
  const pid = poster?.id || 'main';
  const unmaskedPhotoUrl =
    (poster?.userPhotoOriginalUrl && String(poster.userPhotoOriginalUrl).trim())
    || (poster?.surveyAnswers?.user_photo_original && String(poster.surveyAnswers.user_photo_original).trim())
    || null;
  const canTryNewMask = typeof onTryNewMask === 'function' && !!unmaskedPhotoUrl && !!poster?.partnerCutout;
  const cutoutClass = (() => {
    const v = 1 + Math.floor(seedFromString(`feed-cutout-${pid}`) * 3);
    return v === 1 ? '' : ` partner-cutout-jagged-${v}`;
  })();
  const selfGenderShort = formatSelfGenderShort(poster);
  const selfAgeForBadge =
    selfGenderShort && typeof poster?.selfAge === 'number' && Number.isFinite(poster.selfAge)
      ? (() => {
        const n = Math.round(poster.selfAge);
        if (n < 1 || n > 150) return null;
        return String(n);
      })()
      : null;
  const selfGenderWithAge = selfGenderShort
    ? (selfAgeForBadge ? `${selfGenderShort} ${selfAgeForBadge}` : selfGenderShort)
    : null;
  const luvFlags = posterFindLuvIconFlags(poster);
  const showFriendLuv = luvFlags.friend;
  const showRomanticLuv = luvFlags.romantic;
  const romanticMatchGendersLabel = showRomanticLuv
    ? formatLookingForGendersShort(
      Array.isArray(poster.lookingForGenders) && poster.lookingForGenders.length
        ? poster.lookingForGenders
        : null,
    )
    : null;
  const nicknameTileTrailing = !!(selfGenderWithAge || showFriendLuv || showRomanticLuv);
  return (
    <div className="wanted-poster wanted-poster-feed user-menu-main-collage-poster">
      <div className="poster-content">
        <div className="user-menu-main-nickname-tile-wrap">
        <p
          className={`the-one-label the-one-label-feed user-menu-main-nickname-tile${nicknameTileTrailing ? ' user-menu-main-nickname-tile--with-trailing' : ''}`}
          aria-hidden="true"
        >
          <span className="user-menu-main-nickname-quote">
            &quot;{poster.posterNickname ?? 'The One'}&quot;
          </span>
          {nicknameTileTrailing ? (
            <span className="user-menu-main-nickname-trailing">
              {selfGenderWithAge ? (
                <span className="user-menu-main-self-gender">{selfGenderWithAge}</span>
              ) : null}
              {showFriendLuv || showRomanticLuv ? (
                <span className="user-menu-main-luv-icons">
                  {showRomanticLuv ? (
                    <span className="user-menu-main-luv-romantic-wrap">
                      <img src={orheartImage} alt="" className="user-menu-main-luv-icon user-menu-main-luv-icon--romantic" />
                      {romanticMatchGendersLabel ? (
                        <span className="user-menu-main-luv-romantic-letters" aria-hidden="true">
                          {romanticMatchGendersLabel}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                  {showFriendLuv && showRomanticLuv ? (
                    <span className="user-menu-main-luv-divider" aria-hidden="true">|</span>
                  ) : null}
                  {showFriendLuv ? (
                    <img src={friendsImage} alt="" className="user-menu-main-luv-icon user-menu-main-luv-icon--friends" />
                  ) : null}
                </span>
              ) : null}
            </span>
          ) : null}
        </p>
        {canTryNewMask ? (
          <button
            type="button"
            className="user-menu-main-try-mask-btn"
            onClick={() => onTryNewMask(poster)}
            aria-label="Try a new fox mask"
          >
            TRY A NEW MASK
          </button>
        ) : null}
        </div>
        <div className="collage-poster-area collage-poster-area-feed">
          <div className="partner-swag-container partner-swag-container-feed">
            <div className="partner-swag-left">
              {(poster.swags || []).length > 0 && (
                <div className="swag-images-left swag-images-left-feed">
                  {(poster.swags || []).map((swag, swagIndex) => {
                    const swagUrl = swag.imageUrl || swag;
                    if (!swagUrl) return null;
                    const seed = seedFromString(`${pid}-swag-${swagIndex}`);
                    const seedX = seedFromString(`${pid}-swag-x-${swagIndex}`);
                    const rotation = (seed * 30 - 15).toFixed(1);
                    const horizontalOffset = (seedX * 16 - 8).toFixed(1);
                    return (
                      <img
                        key={swag.id || swagIndex}
                        src={swagUrl}
                        alt={swag.name || 'Brand'}
                        className="swag-image swag-image-feed"
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          marginLeft: `${horizontalOffset}px`,
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            <div className="partner-swag-center">
              <div className="partner-swag-center-stack">
                {showFriendFoxMaskPlaceholder(poster) && poster.partnerCutout ? (
                  <FriendFoxMaskPlaceholderCutout
                    variant="feed"
                    jaggedClassName={cutoutClass}
                    heartSrc={poster.heartPhotoUrl || poster.surveyAnswers?.vibe_photo || orheartImage}
                    heartClassName="partner-cutout-gif-overlay partner-cutout-gif-overlay-feed"
                  />
                ) : poster.partnerCutout ? (
                  <div className="final-partner-image-wrapper">
                    <img
                      src={poster.partnerCutout}
                      alt=""
                      className={`final-partner-image final-partner-image-feed${cutoutClass}`}
                    />
                    <img
                      src={poster.heartPhotoUrl || poster.surveyAnswers?.vibe_photo || orheartImage}
                      alt=""
                      className="partner-cutout-gif-overlay partner-cutout-gif-overlay-feed"
                    />
                  </div>
                ) : (
                  <div className={`final-partner-image final-partner-image-placeholder final-partner-image-feed${cutoutClass}`}>
                    👤
                  </div>
                )}
              </div>
            </div>
            <div className="partner-swag-right">
              <div className="activities-emojis-right feed-tile-activities">
                {[poster.activityX, poster.activityY, poster.activityZ].filter(Boolean).map((label, i) => {
                  const seed = seedFromString(`${pid}-act-${i}`);
                  const seedX = seedFromString(`${pid}-act-x-${i}`);
                  const rotation = (seed * 24 - 12).toFixed(1);
                  const horizontalOffset = (seedX * 12 - 6).toFixed(1);
                  const hue = 32 + seed * 20;
                  const bgColor = `hsl(${hue.toFixed(0)}, 90%, 72%)`;
                  return (
                    <div
                      key={i}
                      className="activity-tile-outer activity-tile-outer-feed"
                      style={{
                        transform: `rotate(${rotation}deg)`,
                        marginLeft: `${horizontalOffset}px`,
                      }}
                    >
                      <div className="activity-emoji-wrapper collage-activity-tile" style={{ background: bgColor }}>
                        <div className="activity-emoji activity-emoji-feed">{label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {poster.places && poster.places.length > 0 && (
            <div className="collage-aspirations-row collage-aspirations-row-feed">
              {[
                poster.places.slice(0, Math.floor(seedFromString(`${pid}-places-split`) * 2) + 1),
                poster.places.slice(Math.floor(seedFromString(`${pid}-places-split`) * 2) + 1),
              ].map((rowPlaces, rowIdx) =>
                rowPlaces.length > 0 ? (
                  <div key={rowIdx} className="collage-places-row-inner">
                    {rowPlaces.map((place, i) => (
                      <div key={place.id || `${rowIdx}-${i}`} className="aspiration-tile-outer">
                        <div className="collage-place-tile">
                          <img src={place.imageUrl} alt={place.name} className="collage-place-img collage-place-img-feed" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null,
              )}
            </div>
          )}
        </div>
        {typeof onEditMainCollage === 'function' || typeof onShareMainCollage === 'function' ? (
          <div className="user-menu-main-collage-edit-wrap user-menu-main-collage-edit-wrap--under-cutout">
            {typeof onEditMainCollage === 'function' ? (
              <button
                type="button"
                className="feed-tile-detail-btn feed-tile-detail-btn-refer user-menu-main-collage-edit"
                onClick={() => onEditMainCollage(poster)}
                aria-label="Edit primary collage"
              >
                EDIT
              </button>
            ) : null}
            {typeof onShareMainCollage === 'function' ? (
              <button
                type="button"
                className="feed-tile-detail-btn feed-tile-detail-btn-refer user-menu-main-collage-edit"
                onClick={() => onShareMainCollage(poster)}
                aria-label="Share primary collage"
              >
                SHARE
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Inline "Your collages" list shown on the home page when opened from the user menu (navbar).
 */
export default function UserMenuCollagesSection({
  spinBalanceUserId = null,
  onMatchaExplainerOpen,
  greetingName,
  primaryCollageId = null,
  savedPosters,
  loadingPosters,
  posterDeleteConfirmId,
  onSelectPoster,
  onSetDeleteConfirmId,
  onDeletePoster,
  onEditMainCollage,
  onShareMainCollage,
  onGenerateNewMask,
  onCommitNewMask,
  matchFlowLoading = false,
  onUserMenuPrimaryPromoteSet,
  onShowDinnerGiveawayExplainer,
  rewardsToRedeem = [],
  persistedRewardConciergeKeys = [],
  persistedRewardShare50SentKeys = [],
  onInviteMatchToDailyVibeChecks,
  onInviteFriendByEmailToDailyVibeChecks,
  onRewardConciergeGet,
  onPersistShare50FriendSent,
  onScrollToMatchAFriend,
  /** Same as hero "Collage" — opens wanted flow (parent closes inline collages). */
  onStartCollage,
  vibeAccuracy = null,
  auraRgb = null,
  /** While true the vibe cloud reads "recalculating vibe..." and `auraRgb` is still the pre-edit color. */
  auraRecalculating = false,
  vibeCheckStreak = 0,
  vibeCheckStreakLastPeriodKey = null,
  onVibeCheck,
  onOpenMessages,
  profileVerified = null,
  profileVerificationPending = false,
  profileEmailConfirmed = false,
  eduVerifySendFailed = false,
  onVerifyProfile,
  eduEmailOnFile = '',
  onSaveEduEmail,
  onResendEduEmail,
  eduResendCooldownSeconds = 0,
  eduVerifyBusy = false,
  eduVerifyError = '',
  notificationEmailOnFile = '',
  onSaveNotificationEmail,
}) {
  const name = (greetingName && String(greetingName).trim()) || 'there';
  const mainPoster = Array.isArray(savedPosters) ? getPrimarySavedPoster(savedPosters, primaryCollageId) : null;
  const vibeAccuracyPercent = displayVibeAccuracyPercent(vibeAccuracy, Boolean(mainPoster));
  const auraStyle = useMemo(
    () => (auraRgb ? auraRgbToStyleVars(auraRgb) : undefined),
    [auraRgb],
  );
  const vibeCheckStreakCount = resolveVibeCheckStreakDisplay(
    vibeCheckStreak,
    vibeCheckStreakLastPeriodKey,
  );

  const notificationEmailInputRef = useRef(null);
  const mainCollageAuraZoneRef = useRef(null);
  const notificationEmailOnFileRef = useRef(notificationEmailOnFile);
  const [notificationEmailInput, setNotificationEmailInput] = useState(
    () => notificationEmailOnFile || '',
  );
  const [notificationEmailEditing, setNotificationEmailEditing] = useState(false);
  const [notificationEmailError, setNotificationEmailError] = useState('');
  const [notificationEmailSaving, setNotificationEmailSaving] = useState(false);
  const [showPaidForDateExplainer, setShowPaidForDateExplainer] = useState(false);
  /** Poster awaiting "TRY A NEW MASK" confirmation (null = popup closed). */
  const [tryNewMaskPoster, setTryNewMaskPoster] = useState(null);
  const [tryNewMaskRunning, setTryNewMaskRunning] = useState(false);
  /** OLD vs NEW mask comparison popup: { poster, oldMaskUrl, newMaskUrl } | null. */
  const [maskComparison, setMaskComparison] = useState(null);
  const [maskUpdating, setMaskUpdating] = useState(false);

  const runTryNewMask = async () => {
    if (!tryNewMaskPoster || tryNewMaskRunning || typeof onGenerateNewMask !== 'function') return;
    setTryNewMaskRunning(true);
    try {
      const poster = tryNewMaskPoster;
      const newMaskUrl = await onGenerateNewMask(poster);
      if (newMaskUrl) {
        setTryNewMaskPoster(null);
        setMaskComparison({
          poster,
          oldMaskUrl: poster.partnerCutout || null,
          newMaskUrl,
        });
      }
    } finally {
      setTryNewMaskRunning(false);
    }
  };

  const confirmMaskUpdate = async () => {
    if (!maskComparison || maskUpdating || typeof onCommitNewMask !== 'function') return;
    setMaskUpdating(true);
    try {
      const ok = await onCommitNewMask(maskComparison.poster, maskComparison.newMaskUrl);
      if (ok !== false) setMaskComparison(null);
    } finally {
      setMaskUpdating(false);
    }
  };

  /** This month's daily-check GIFs keyed by periodKey, for the daily vibes carousel (same as FoxChat). */
  const [monthVibeGifs, setMonthVibeGifs] = useState({});
  const dailyVibesCarouselRef = useRef(null);
  const dailyVibesUserId = spinBalanceUserId || auth.currentUser?.uid || null;
  const [inviteFriendOpen, setInviteFriendOpen] = useState(false);
  const [selectedDailyVibeFriend, setSelectedDailyVibeFriend] = useState(null);
  /** Accepted vibe-share partners (their uids) + per-uid nickname and this month's checks. */
  const [acceptedFriendUids, setAcceptedFriendUids] = useState([]);
  const [friendNicknameByUid, setFriendNicknameByUid] = useState({});
  const [friendAuraStyleByUid, setFriendAuraStyleByUid] = useState({});
  const [friendMonthVibeGifsByUid, setFriendMonthVibeGifsByUid] = useState({});

  /** Day cards: 1..today (vibe-period day of the current month). */
  const { dailyVibesCarouselDays, dailyVibesMonthLabel, dailyVibesMonthShort } = useMemo(() => {
    const currentKey = getDailyVibePeriodKey();
    const monthPrefix = currentKey.slice(0, 8);
    const todayDay = Number(currentKey.slice(8));
    const [year, month] = currentKey.slice(0, 7).split('-').map(Number);
    const monthName = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' });
    const days = Array.from({ length: todayDay }, (_, i) => {
      const day = i + 1;
      return { day, periodKey: `${monthPrefix}${String(day).padStart(2, '0')}` };
    });
    return {
      dailyVibesCarouselDays: days,
      dailyVibesMonthLabel: `Your daily vibes in ${monthName}`,
      dailyVibesMonthShort: monthName.slice(0, 3).toUpperCase(),
    };
  }, []);

  useEffect(() => {
    if (!dailyVibesUserId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const byDay = await loadDailyVibeChecksForMonth(db, dailyVibesUserId);
        if (!cancelled) setMonthVibeGifs(byDay);
      } catch (err) {
        if (!cancelled) console.warn('Daily vibe month load failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dailyVibesUserId]);

  /* Live-update today's vibe check so a freshly sent GIF (from the FoxChat
     launcher or the Messages tab) appears in the grid without a page refresh. */
  useEffect(() => {
    if (!dailyVibesUserId) return undefined;
    const periodKey = getDailyVibePeriodKey();
    return subscribeToDailyVibeCheckForPeriod(db, dailyVibesUserId, periodKey, (check) => {
      setMonthVibeGifs((prev) => {
        if (!check?.gifUrl) {
          if (!prev[periodKey]) return prev;
          const next = { ...prev };
          delete next[periodKey];
          return next;
        }
        const existing = prev[periodKey];
        if (existing && existing.gifUrl === check.gifUrl && existing.stillUrl === (check.stillUrl || null)) {
          return prev;
        }
        return {
          ...prev,
          [periodKey]: { gifUrl: check.gifUrl, stillUrl: check.stillUrl || null },
        };
      });
    });
  }, [dailyVibesUserId]);

  /* Keep the carousel scrolled to the latest day. */
  useEffect(() => {
    const el = dailyVibesCarouselRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [monthVibeGifs, friendMonthVibeGifsByUid]);

  /* Who am I currently sharing daily vibe checks with (all accepted partners)? */
  useEffect(() => {
    if (!dailyVibesUserId) {
      setAcceptedFriendUids([]);
      return undefined;
    }
    return subscribeToAcceptedDailyVibeShares(db, dailyVibesUserId, (uids) => {
      setAcceptedFriendUids(uids);
    });
  }, [dailyVibesUserId]);

  const acceptedFriendUidsKey = acceptedFriendUids.join(',');

  /* Each accepted friend's nickname (from their public primary collage) for the row label. */
  useEffect(() => {
    if (acceptedFriendUids.length === 0) {
      setFriendNicknameByUid({});
      return undefined;
    }
    const unsubs = acceptedFriendUids.map((uid) =>
      onSnapshot(
        doc(db, 'userPublicPrimary', uid),
        (snap) => {
          const raw = snap.exists() ? snap.data()?.ad : null;
          const nickname =
            raw && typeof raw.posterNickname === 'string' && raw.posterNickname.trim()
              ? raw.posterNickname.trim()
              : null;
          setFriendNicknameByUid((prev) => {
            if (prev[uid] === nickname) return prev;
            return { ...prev, [uid]: nickname };
          });
          // Only show the aura when we can read their real stored color; otherwise none.
          const auraRgb = parseAuraRgb(raw?.auraColor);
          const auraStyleVars = auraRgb ? auraRgbToStyleVars(auraRgb) : null;
          setFriendAuraStyleByUid((prev) => ({ ...prev, [uid]: auraStyleVars }));
        },
        () => {
          setFriendNicknameByUid((prev) => {
            if (!(uid in prev)) return prev;
            const next = { ...prev };
            delete next[uid];
            return next;
          });
          setFriendAuraStyleByUid((prev) => {
            if (!(uid in prev)) return prev;
            const next = { ...prev };
            delete next[uid];
            return next;
          });
        },
      ),
    );
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFriendUidsKey]);

  /* Each accepted friend's daily vibe checks for this month (readable once accepted). */
  useEffect(() => {
    if (acceptedFriendUids.length === 0) {
      setFriendMonthVibeGifsByUid({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        acceptedFriendUids.map(async (uid) => {
          try {
            const byDay = await loadDailyVibeChecksForMonth(db, uid);
            return [uid, byDay];
          } catch (err) {
            console.warn('Friend daily vibe month load failed:', err);
            return [uid, {}];
          }
        }),
      );
      if (!cancelled) setFriendMonthVibeGifsByUid(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFriendUidsKey]);

  /* Friend rows to render: accepted partners (with their GIFs) first, then a
     freshly-picked pending invite (empty GIFs), and at minimum one placeholder. */
  const friendVibeRows = useMemo(() => {
    const rows = acceptedFriendUids.map((uid) => ({
      key: uid,
      uid,
      nickname: friendNicknameByUid[uid] || 'Your friend',
      gifs: friendMonthVibeGifsByUid[uid] || {},
      auraStyle: friendAuraStyleByUid[uid] || null,
    }));
    const pendingUid = selectedDailyVibeFriend?.uid || null;
    const pendingAlreadyAccepted = pendingUid && acceptedFriendUids.includes(pendingUid);
    if (selectedDailyVibeFriend && !pendingAlreadyAccepted) {
      rows.push({
        key: pendingUid || 'pending-friend',
        uid: pendingUid,
        nickname: selectedDailyVibeFriend.nickname || 'Your friend',
        gifs: {},
        auraStyle: null,
      });
    }
    if (rows.length === 0) {
      rows.push({ key: 'friend-placeholder', uid: null, nickname: 'Your friend', gifs: {}, auraStyle: null });
    }
    return rows;
  }, [acceptedFriendUids, friendNicknameByUid, friendMonthVibeGifsByUid, friendAuraStyleByUid, selectedDailyVibeFriend]);

  /* OPEN MESSAGES (verified profiles only): inbox after today's vibe check; otherwise Fox Chat. */
  const handleOpenMessagesClick = async () => {
    if (typeof onOpenMessages !== 'function') return;
    const uid = dailyVibesUserId;
    const periodKey = getDailyVibePeriodKey();
    const completedToday = Boolean(
      monthVibeGifs[periodKey]?.gifUrl || (uid && hasDailyVibeCheckForPeriod(uid)),
    );
    if (completedToday) {
      onOpenMessages();
      return;
    }
    if (uid) {
      try {
        const check = await loadDailyVibeCheckForPeriod(db, uid, periodKey);
        if (check?.gifUrl) {
          onOpenMessages();
          return;
        }
      } catch (_) {
        /* fall through to Fox Chat */
      }
    }
    if (typeof onVibeCheck === 'function') {
      onVibeCheck();
      return;
    }
    onOpenMessages();
  };

  useEffect(() => {
    const next = notificationEmailOnFile || '';
    const prev = notificationEmailOnFileRef.current || '';
    notificationEmailOnFileRef.current = next;
    if (prev !== next) {
      setNotificationEmailInput(next);
      setNotificationEmailEditing(false);
      setNotificationEmailError('');
    }
  }, [notificationEmailOnFile]);

  useEffect(() => {
    if (!notificationEmailEditing) return;
    const input = notificationEmailInputRef.current;
    if (!input) return;
    const id = window.requestAnimationFrame(() => {
      input.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [notificationEmailEditing]);

  const startNotificationEmailEdit = () => {
    setNotificationEmailInput(notificationEmailOnFile || '');
    setNotificationEmailError('');
    setNotificationEmailEditing(true);
  };
  /** Open reward detail / concierge popup; holds the clicked reward object */
  const [rewardDetailReward, setRewardDetailReward] = useState(null);
  /** Spin prize "$50 for your friend" — email / IG flow (feed-style) */
  const [share50ModalReward, setShare50ModalReward] = useState(null);
  const [share50View, setShare50View] = useState('pick');
  const [share50Email, setShare50Email] = useState('');
  const [share50EmailError, setShare50EmailError] = useState('');
  const [share50EmailSubmitted, setShare50EmailSubmitted] = useState(false);
  const [share50Ig, setShare50Ig] = useState('');
  const [share50IgError, setShare50IgError] = useState('');
  const [share50IgSubmitted, setShare50IgSubmitted] = useState(false);
  const [share50Saving, setShare50Saving] = useState(false);

  const isRewardConciergeRequested = (r) => {
    const k = rewardTilePersistenceKey(r);
    return Boolean(k && persistedRewardConciergeKeys.includes(k));
  };

  const isShare50FriendSent = (r) => {
    if (!isShare50ForFriendReward(r)) return false;
    const k = rewardTilePersistenceKey(r);
    return Boolean(k && persistedRewardShare50SentKeys.includes(k));
  };

  const resetShare50ModalFields = () => {
    setShare50View('pick');
    setShare50Email('');
    setShare50EmailError('');
    setShare50EmailSubmitted(false);
    setShare50Ig('');
    setShare50IgError('');
    setShare50IgSubmitted(false);
    setShare50Saving(false);
  };

  const closeShare50Modal = () => {
    setShare50ModalReward(null);
    resetShare50ModalFields();
  };

  const rewardsListForMenu = useMemo(() => {
    const list = Array.isArray(rewardsToRedeem) ? [...rewardsToRedeem] : [];
    if (!spinBalanceUserId) return list;
    const freeMatchaTile = {
      id: 'free-matcha',
      source: 'freeMatcha',
      title: 'MATCH A FRIEND - GET A SPIN',
      description: 'refer a friend to a Featured Luv Pack',
    };
    return [freeMatchaTile, ...list];
  }, [spinBalanceUserId, rewardsToRedeem]);

  return (
    <div className="user-menu-collages">
      <div className="user-menu-collages-greeting-row">
        <div className="user-menu-collages-greeting-stack">
          <p className="user-menu-collages-greeting">
            <span className="user-menu-collages-greeting-inline">
              <span className="user-menu-collages-greeting-text-line">
                <span className="user-menu-collages-greeting-hello">Hello, </span>
                <span className="user-menu-collages-greeting-name">{name}!</span>
              </span>
              <img src={theFoxMini} alt="" className="user-menu-collages-greeting-icon" aria-hidden="true" />
            </span>
          </p>
          <p className="user-menu-collages-greeting-tagline">
            Our visual-vibe transformer analyzes visuals to find people that match your vibe. We match only verified profiles. 
          </p>
          {profileVerified === true ? (
            <>
              <p className="user-menu-profile-verified">
                <span className="match-flow-check-icon" aria-hidden="true">✓</span>
                Your profile is verified
              </p>
              {typeof onOpenMessages === 'function' ? (
                <div className="user-menu-friend-vibes-invite-wrap user-menu-profile-verified-messages-wrap">
                  <button
                    type="button"
                    className="user-menu-collage-vibe-check-btn"
                    onClick={handleOpenMessagesClick}
                  >
                    OPEN MESSAGES
                  </button>
                </div>
              ) : null}
            </>
          ) : profileVerificationPending ? (
            <div className="user-menu-profile-verification-pending-wrap">
              {profileEmailConfirmed ? (
                <p className="user-menu-profile-verification-pending">
                  Your .edu email is confirmed and your profile is being verified. This can take up to 24 hours. We only match real people.
                </p>
              ) : (
                <div className="user-menu-profile-verification-pending-copy">
                  {eduVerifySendFailed ? (
                    <p className="user-menu-profile-verification-pending user-menu-coupon-redeem-error">
                      We couldn&apos;t deliver the verification link to this address. Tap RESEND to try again.
                    </p>
                  ) : (
                    <p className="user-menu-profile-verification-pending">
                      We sent a link to verify your .edu email. Confirm your email.
                    </p>
                  )}
                  <p className="user-menu-profile-verification-pending">
                    We use your .edu email as a reliable way to verify you are a real person before you get matched.
                  </p>
                </div>
              )}
              {!profileEmailConfirmed ? (
                <PendingEduEmailField
                  email={eduEmailOnFile}
                  cooldownSeconds={eduResendCooldownSeconds}
                  busy={eduVerifyBusy}
                  error={eduVerifyError}
                  onSave={onSaveEduEmail}
                  onResend={onResendEduEmail}
                  variant="user-menu"
                />
              ) : null}
            </div>
          ) : profileVerified === false && typeof onVerifyProfile === 'function' ? (
            <div className="user-menu-profile-verify-wrap">
              <button
                type="button"
                className="user-menu-collage-vibe-check-btn user-menu-profile-verify-btn"
                onClick={onVerifyProfile}
              >
                Verify profile
              </button>
            </div>
          ) : null}
          {spinBalanceUserId ? (
            <>
            <section className="user-menu-notification-prefs" aria-labelledby="user-menu-notification-prefs-heading">
              <div className="user-menu-collages-header user-menu-notification-prefs-header">
                <h2 id="user-menu-notification-prefs-heading" className="user-menu-collages-title">
                  NOTIFICATION PREFERENCES
                </h2>
              </div>
              <p className="user-menu-notification-prefs-hint">
                your match messages will be redirected to:
              </p>
              {notificationEmailEditing ? (
                <form
                  className="user-menu-notification-prefs-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (notificationEmailSaving) return;
                    const err = notificationEmailValidationError(notificationEmailInput);
                    if (err) {
                      setNotificationEmailError(err);
                      return;
                    }
                    if (typeof onSaveNotificationEmail !== 'function') return;
                    const trimmed = String(notificationEmailInput).trim().toLowerCase();
                    if (trimmed === String(notificationEmailOnFile || '').trim().toLowerCase()) {
                      setNotificationEmailError('');
                      setNotificationEmailEditing(false);
                      return;
                    }
                    setNotificationEmailSaving(true);
                    setNotificationEmailError('');
                    try {
                      const ok = await onSaveNotificationEmail(trimmed);
                      if (!ok) {
                        setNotificationEmailError('Could not save. Try again.');
                      } else {
                        setNotificationEmailEditing(false);
                      }
                    } finally {
                      setNotificationEmailSaving(false);
                    }
                  }}
                >
                  <input
                    ref={notificationEmailInputRef}
                    id="user-menu-notification-email"
                    type="email"
                    className="user-menu-notification-prefs-email-field"
                    aria-label="Notification email"
                    value={notificationEmailInput}
                    onChange={(e) => {
                      setNotificationEmailInput(e.target.value);
                      if (notificationEmailError) setNotificationEmailError('');
                    }}
                    autoComplete="email"
                    disabled={notificationEmailSaving}
                  />
                  {notificationEmailError ? (
                    <p className="user-menu-coupon-redeem-error">{notificationEmailError}</p>
                  ) : null}
                  <div className="user-menu-notification-prefs-actions">
                    <button
                      type="submit"
                      className="user-menu-notification-prefs-save-btn"
                      disabled={notificationEmailSaving}
                    >
                      {notificationEmailSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="user-menu-notification-prefs-cancel-btn"
                      disabled={notificationEmailSaving}
                      onClick={() => {
                        setNotificationEmailInput(notificationEmailOnFile || '');
                        setNotificationEmailError('');
                        setNotificationEmailEditing(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="user-menu-notification-prefs-form">
                  <p
                    id="user-menu-notification-email"
                    className="user-menu-notification-prefs-email-field"
                    aria-label="Notification email"
                  >
                    {notificationEmailOnFile || '—'}
                  </p>
                  {notificationEmailError ? (
                    <p className="user-menu-coupon-redeem-error">{notificationEmailError}</p>
                  ) : null}
                  <div className="user-menu-notification-prefs-edit-wrap">
                    <button
                      type="button"
                      className="feed-tile-detail-btn feed-tile-detail-btn-refer user-menu-notification-prefs-edit-btn"
                      aria-label="Edit notification email"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startNotificationEmailEdit();
                      }}
                    >
                      EDIT
                    </button>
                  </div>
                </div>
              )}
            </section>
            </>
          ) : null}
        </div>
      </div>

      {!loadingPosters && (
        <section
          ref={mainCollageAuraZoneRef}
          className="user-menu-main-collage"
          aria-label="Main collage"
          style={auraStyle}
        >
          {mainPoster ? (
            <>
              <div className="user-menu-main-collage-wrap">
                <UserMenuMainCollagePreview
                  poster={mainPoster}
                  onEditMainCollage={onEditMainCollage}
                  onShareMainCollage={onShareMainCollage}
                  onTryNewMask={
                    typeof onGenerateNewMask === 'function'
                      ? (poster) => setTryNewMaskPoster(poster)
                      : undefined
                  }
                />
              </div>
              <HeroMatrixAura
                className="user-menu-collage-matrix-aura"
                auraRgb={auraRgb}
                auraZoneRef={mainCollageAuraZoneRef}
                showIcon={false}
                cloudHint={
                  auraRecalculating ? (
                    <>
                      recalculating
                      <br />
                      vibe...
                    </>
                  ) : (
                    'your vibe'
                  )
                }
                cloudHintClassName={auraRecalculating ? 'hero-aura-cloud__letters--status' : ''}
                showLabel={false}
              />
              {vibeAccuracyPercent != null ? (
                <>
                  <p className="user-menu-collage-vibe-accuracy">
                    Vibe accuracy:{' '}
                    <span className="user-menu-collage-vibe-accuracy-value">
                      {vibeAccuracyPercent}%
                    </span>
                  </p>
                  {typeof onVibeCheck === 'function' ? (
                    <button
                      type="button"
                      className="user-menu-collage-vibe-accuracy-hint user-menu-collage-vibe-accuracy-hint-btn"
                      onClick={onVibeCheck}
                    >
                      add your daily gif to improve accuracy and get new{' '}
                      <span className="user-menu-collage-vibe-check-streak-hint-tail">
                        matches
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="user-menu-collage-vibe-check-streak-hint-arrow"
                          aria-hidden
                        >
                          <path d="M7 17 17 7M7 7h10v10" />
                        </svg>
                      </span>
                    </button>
                  ) : (
                    <p className="user-menu-collage-vibe-accuracy-hint">
                      add your daily gif to improve accuracy and get new matches
                    </p>
                  )}
                  <div className="user-menu-collages-header user-menu-friend-vibes-header">
                    <h2 className="user-menu-collages-title">YOUR DAILY VIBE GIFS</h2>
                  </div>
                  {typeof onVibeCheck === 'function' ? (
                    <div className="user-menu-collage-vibe-check-wrap">
                      <p className="user-menu-collage-vibe-check-streak">
                        {vibeCheckStreakCount}/{VIBE_CHECK_STREAK_MAX} VIBE STREAK{' '}
                        <button
                          type="button"
                          className="user-menu-collage-vibe-check-streak-help saved-poster-match-help"
                          aria-label="How vibe check streaks work"
                          onClick={() => setShowPaidForDateExplainer(true)}
                        >
                          ?
                        </button>
                      </p>
                    </div>
                  ) : null}
                  <div className="fox-chat-streak-carousel-wrap user-menu-daily-vibes-carousel-wrap">
                    <div className="user-menu-daily-vibes-row">
                      <div className="user-menu-daily-vibes-row-labels" aria-hidden="true">
                        <div className="user-menu-daily-vibes-row-label-spacer" />
                        <span className="user-menu-daily-vibes-row-label">
                          {auraStyle ? (
                            <span className="user-menu-daily-vibes-you-aura" aria-hidden="true">
                              <span className="hero-aura-cloud__blob hero-aura-cloud__blob--one" />
                              <span className="hero-aura-cloud__blob hero-aura-cloud__blob--two" />
                              <span className="hero-aura-cloud__blob hero-aura-cloud__blob--three" />
                            </span>
                          ) : null}
                          <span className="user-menu-daily-vibes-row-label-text">You</span>
                        </span>
                        {friendVibeRows.map((row) => (
                          <span
                            key={row.key}
                            className="user-menu-daily-vibes-row-label user-menu-daily-vibes-row-label--friend"
                          >
                            {row.auraStyle ? (
                              <span
                                className="user-menu-daily-vibes-you-aura"
                                style={row.auraStyle}
                                aria-hidden="true"
                              >
                                <span className="hero-aura-cloud__blob hero-aura-cloud__blob--one" />
                                <span className="hero-aura-cloud__blob hero-aura-cloud__blob--two" />
                                <span className="hero-aura-cloud__blob hero-aura-cloud__blob--three" />
                              </span>
                            ) : null}
                            <span className="user-menu-daily-vibes-row-label-text user-menu-daily-vibes-row-label-text--nickname">
                              {row.nickname}
                            </span>
                          </span>
                        ))}
                      </div>
                      <div
                        className="fox-chat-streak-carousel"
                        ref={dailyVibesCarouselRef}
                        role="list"
                        aria-label={`Daily vibe checks in ${dailyVibesMonthLabel}`}
                      >
                      {dailyVibesCarouselDays.map(({ day, periodKey }) => {
                        const dayGif = monthVibeGifs[periodKey];
                        const isToday = day === dailyVibesCarouselDays.length;
                        return (
                          <div
                            key={periodKey}
                            className="user-menu-daily-vibes-day-col"
                            role="listitem"
                            aria-label={dayGif ? `Day ${day}: vibe check sent` : `Day ${day}: no vibe check`}
                          >
                            <div
                              className={`fox-chat-streak-day${dayGif ? ' fox-chat-streak-day--filled' : ''}${isToday ? ' user-menu-daily-vibes-day-card--today' : ''}`}
                            >
                              <span className="fox-chat-streak-day-month">
                                {dailyVibesMonthShort}
                              </span>
                              <span className="fox-chat-streak-day-num">{day}</span>
                            </div>
                            {dayGif ? (
                              <img
                                src={dayGif.stillUrl || dayGif.gifUrl}
                                alt=""
                                className="user-menu-daily-vibes-day-gif"
                                loading="lazy"
                              />
                            ) : isToday && typeof onVibeCheck === 'function' ? (
                              /* Today's own tile is still empty — double it as the vibe check entry point. */
                              <button
                                type="button"
                                className="user-menu-daily-vibes-day-gif user-menu-daily-vibes-day-gif--empty user-menu-daily-vibes-day-gif--add"
                                onClick={onVibeCheck}
                                aria-label="Pick your vibe for today"
                              >
                                <span className="user-menu-daily-vibes-day-add-plus">+</span>
                                <span className="user-menu-daily-vibes-day-add-label">add</span>
                              </button>
                            ) : (
                              <div
                                className="user-menu-daily-vibes-day-gif user-menu-daily-vibes-day-gif--empty"
                                aria-hidden="true"
                              />
                            )}
                            {friendVibeRows.map((row, rowIndex) => {
                              const friendDayGif = row.gifs[periodKey];
                              const isLastFriendRow = rowIndex === friendVibeRows.length - 1;
                              const canInviteFriend =
                                typeof onInviteMatchToDailyVibeChecks === 'function'
                                || typeof onInviteFriendByEmailToDailyVibeChecks === 'function';
                              if (friendDayGif) {
                                return (
                                  <img
                                    key={row.key}
                                    src={friendDayGif.stillUrl || friendDayGif.gifUrl}
                                    alt=""
                                    className="user-menu-daily-vibes-day-gif"
                                    loading="lazy"
                                  />
                                );
                              }
                              if (isToday && isLastFriendRow && canInviteFriend) {
                                return (
                                  <button
                                    key={row.key}
                                    type="button"
                                    className="user-menu-daily-vibes-day-gif user-menu-daily-vibes-day-gif--empty user-menu-daily-vibes-day-gif--add"
                                    onClick={() => setInviteFriendOpen(true)}
                                    aria-label="Invite a friend to see their daily vibes"
                                  >
                                    <span className="user-menu-daily-vibes-day-add-plus">+</span>
                                    <span className="user-menu-daily-vibes-day-add-label">add</span>
                                  </button>
                                );
                              }
                              return (
                                <div
                                  key={row.key}
                                  className="user-menu-daily-vibes-day-gif user-menu-daily-vibes-day-gif--empty"
                                  aria-hidden="true"
                                />
                              );
                            })}
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  </div>
                  {typeof onInviteMatchToDailyVibeChecks === 'function'
                  || typeof onInviteFriendByEmailToDailyVibeChecks === 'function' ? (
                    <div className="user-menu-friend-vibes-invite-wrap">
                      <button
                        type="button"
                        className="user-menu-collage-vibe-check-btn"
                        onClick={() => {
                          setInviteFriendOpen(true);
                        }}
                      >
                        SEE FRIEND'S VIBES
                      </button>
                    </div>
                  ) : null}
                  <p className="user-menu-friend-vibes-hint">
                    invite a friend to compare vibes
                  </p>
                </>
              ) : null}
            </>
          ) : savedPosters.length === 0 && typeof onStartCollage === 'function' ? (
            <div className="user-menu-main-collage-empty-actions">
              <button
                type="button"
                className="poster-collage-btn"
                onClick={(e) => {
                  e.preventDefault();
                  onStartCollage(e);
                }}
              >
                Pick your visuals
              </button>
              <p className="user-menu-main-collage-empty-actions-hint">
                pick visuals to set up your visual-vibe transformer and get a vibe match
              </p>
            </div>
          ) : (
            <p className="user-menu-main-collage-empty">
              {savedPosters.length === 0
                ? 'Save a collage first, then set it as your primary.'
                : 'Choose which saved collage is your primary to show it here.'}
            </p>
          )}
        </section>
      )}

      <section id="user-menu-your-rewards" className="user-menu-rewards" aria-labelledby="user-menu-rewards-heading">
        <div className="user-menu-collages-header user-menu-rewards-header">
          <h2 id="user-menu-rewards-heading" className="user-menu-collages-title">
            WIN A FOX-PAID DATE
          </h2>
        </div>
        <div className="user-menu-rewards-spin-stack">
          <SpinWheelInline
            userId={spinBalanceUserId}
            maxSize={244}
            rewardsToRedeemCount={Array.isArray(rewardsToRedeem) ? rewardsToRedeem.length : 0}
            triggerButtonClassName="user-menu-collage-vibe-check-btn user-menu-rewards-spin-btn"
            onOutOfSpinsClick={typeof onVibeCheck === 'function' ? onVibeCheck : undefined}
          />
        </div>
        {rewardsListForMenu.length > 0 ? (
          <ul className="user-menu-rewards-list">
            {rewardsListForMenu.map((reward, index) => {
              const isFreeMatcha = reward?.source === 'freeMatcha';
              const share50Sent = !isFreeMatcha && isShare50FriendSent(reward);
              const conciergeRequested =
                !isFreeMatcha && !share50Sent && isRewardConciergeRequested(reward);
              const desc = share50Sent
                ? 'sent to friend'
                : conciergeRequested
                  ? 'you will receive instructions over email'
                  : reward.description;
              const staticRewardTile = conciergeRequested || share50Sent;
              return (
                <li key={reward.id || `reward-${index}`} className="user-menu-rewards-list-item">
                  {staticRewardTile ? (
                    <div
                      className="user-menu-rewards-item user-menu-rewards-item--tile-static user-menu-rewards-item--concierge-requested"
                      role="status"
                      aria-label={
                        share50Sent
                          ? `${reward.title || 'Reward'}: sent to friend`
                          : `${reward.title || 'Reward'}: you will receive instructions over email`
                      }
                    >
                      <span className="user-menu-rewards-item-title">{reward.title || 'Reward'}</span>
                      {desc ? <span className="user-menu-rewards-item-desc">{desc}</span> : null}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="user-menu-rewards-item user-menu-rewards-item--tile-btn"
                      onClick={() => {
                        if (isFreeMatcha) {
                          if (typeof onMatchaExplainerOpen === 'function') onMatchaExplainerOpen();
                          return;
                        }
                        if (isShare50ForFriendReward(reward)) {
                          resetShare50ModalFields();
                          setShare50ModalReward(reward);
                          return;
                        }
                        setRewardDetailReward(reward);
                      }}
                    >
                      <span className="user-menu-rewards-item-title">{reward.title || 'Reward'}</span>
                      {desc ? <span className="user-menu-rewards-item-desc">{desc}</span> : null}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>

      <InviteFriendDailyVibeFlow
        open={inviteFriendOpen}
        onClose={() => setInviteFriendOpen(false)}
        userId={dailyVibesUserId}
        onInviteMatch={onInviteMatchToDailyVibeChecks}
        onInviteByEmail={onInviteFriendByEmailToDailyVibeChecks}
        onMatchInvited={setSelectedDailyVibeFriend}
      />

      {share50ModalReward &&
        createPortal(
          <div
            className="user-menu-match-removal-confirm-overlay"
            role="presentation"
            onClick={closeShare50Modal}
          >
            <div
              className="user-menu-match-removal-confirm user-menu-share50-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby={
                share50View === 'email'
                  ? 'user-menu-share50-email-title'
                  : share50View === 'ig'
                    ? 'user-menu-share50-ig-title'
                    : 'user-menu-share50-title'
              }
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cart-popup-close"
                aria-label="Close"
                onClick={closeShare50Modal}
              >
                ×
              </button>
              <div className="shared-poster-apply-content user-menu-share50-refer-flow">
                {share50View === 'email' ? (
                  <>
                    <button
                      type="button"
                      className="feed-tile-detail-back"
                      onClick={() => {
                        setShare50View('pick');
                        setShare50Email('');
                        setShare50EmailError('');
                        setShare50EmailSubmitted(false);
                      }}
                      aria-label="Back"
                    >
                      ‹ Back
                    </button>
                    <h2 id="user-menu-share50-email-title" className="feed-tile-detail-title">
                      Enter your friend&apos;s email
                    </h2>
                    <form
                      className="feed-tile-detail-apply-form"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (share50Saving || share50EmailSubmitted) return;
                        const err = referFriendEmailValidationError(share50Email);
                        if (err) {
                          setShare50EmailError(err);
                          return;
                        }
                        setShare50EmailError('');
                        setShare50Saving(true);
                        try {
                          const email = String(share50Email).trim().toLowerCase();
                          await addDoc(collection(db, 'referSubmissions'), {
                            posterId: primaryCollageId || null,
                            friendEmail: email,
                            source: 'share50ForDate',
                            createdAt: serverTimestamp(),
                            userId: auth.currentUser?.uid || null,
                          });
                          if (share50ModalReward && typeof onPersistShare50FriendSent === 'function') {
                            await onPersistShare50FriendSent(share50ModalReward);
                          }
                          setShare50EmailSubmitted(true);
                        } catch (err) {
                          console.error('share50 email submission:', err);
                          setShare50EmailError('Something went wrong. Please try again.');
                        } finally {
                          setShare50Saving(false);
                        }
                      }}
                    >
                      <input
                        type="email"
                        className="feed-tile-detail-apply-input"
                        placeholder="friend@example.com"
                        value={share50Email}
                        onChange={(e) => {
                          setShare50Email(e.target.value);
                          if (share50EmailError) setShare50EmailError('');
                        }}
                        aria-label="Friend's email"
                        autoComplete="email"
                        disabled={share50EmailSubmitted || share50Saving}
                      />
                      {share50EmailError ? (
                        <p className="feed-tile-detail-apply-error" role="alert">
                          {share50EmailError}
                        </p>
                      ) : null}
                      <button
                        type="submit"
                        className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                        disabled={share50EmailSubmitted || share50Saving}
                      >
                        {share50EmailSubmitted ? 'Received!' : share50Saving ? 'Sending…' : 'Send'}
                      </button>
                    </form>
                    {share50EmailSubmitted ? (
                      <p className="feed-tile-detail-thank-you">We&apos;ll send them an invite!</p>
                    ) : null}
                  </>
                ) : share50View === 'ig' ? (
                  <>
                    <button
                      type="button"
                      className="feed-tile-detail-back"
                      onClick={() => {
                        setShare50View('pick');
                        setShare50Ig('');
                        setShare50IgError('');
                        setShare50IgSubmitted(false);
                      }}
                      aria-label="Back"
                    >
                      ‹ Back
                    </button>
                    <h2 id="user-menu-share50-ig-title" className="feed-tile-detail-title">
                      Enter their IG handle
                    </h2>
                    <form
                      className="feed-tile-detail-apply-form"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (share50Saving || share50IgSubmitted) return;
                        const handle = (share50Ig || '').trim().replace(/^@/, '');
                        if (!handle) {
                          setShare50IgError('Please enter an IG handle.');
                          return;
                        }
                        setShare50IgError('');
                        setShare50Saving(true);
                        try {
                          await addDoc(collection(db, 'referSubmissions'), {
                            posterId: primaryCollageId || null,
                            friendIgHandle: handle,
                            source: 'share50ForDate',
                            createdAt: serverTimestamp(),
                            userId: auth.currentUser?.uid || null,
                          });
                          if (share50ModalReward && typeof onPersistShare50FriendSent === 'function') {
                            await onPersistShare50FriendSent(share50ModalReward);
                          }
                          setShare50IgSubmitted(true);
                        } catch (err) {
                          console.error('share50 IG submission:', err);
                          setShare50IgError('Something went wrong. Please try again.');
                        } finally {
                          setShare50Saving(false);
                        }
                      }}
                    >
                      <input
                        type="text"
                        className="feed-tile-detail-apply-input"
                        placeholder="@username"
                        value={share50Ig}
                        onChange={(e) => {
                          setShare50Ig(e.target.value);
                          if (share50IgError) setShare50IgError('');
                        }}
                        aria-label="Friend's IG handle"
                        autoComplete="off"
                        disabled={share50IgSubmitted || share50Saving}
                      />
                      {share50IgError ? (
                        <p className="feed-tile-detail-apply-error" role="alert">
                          {share50IgError}
                        </p>
                      ) : null}
                      <button
                        type="submit"
                        className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                        disabled={share50IgSubmitted || share50Saving}
                      >
                        {share50IgSubmitted ? 'Received!' : share50Saving ? 'Sending…' : 'Send'}
                      </button>
                    </form>
                    {share50IgSubmitted ? (
                      <p className="feed-tile-detail-thank-you">We&apos;ll reach out to them!</p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <h2 id="user-menu-share50-title" className="feed-tile-detail-title">
                      SHARE $50 FOR A DATE
                    </h2>
                    <p className="feed-tile-detail-apply-prompt">
                      We&apos;ll send your friend an invite to join.
                    </p>
                    <div className="shared-poster-refer-options">
                      <button
                        type="button"
                        className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                        onClick={() => {
                          setShare50View('email');
                          setShare50Email('');
                          setShare50EmailError('');
                          setShare50EmailSubmitted(false);
                        }}
                      >
                        Enter your friend&apos;s email
                      </button>
                      <button
                        type="button"
                        className="feed-tile-detail-btn feed-tile-detail-btn-ig"
                        onClick={() => {
                          setShare50View('ig');
                          setShare50Ig('');
                          setShare50IgError('');
                          setShare50IgSubmitted(false);
                        }}
                      >
                        Enter their IG handle
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {rewardDetailReward &&
        createPortal(
          <div
            className="user-menu-match-removal-confirm-overlay"
            role="presentation"
            onClick={() => setRewardDetailReward(null)}
          >
            <div
              className="user-menu-match-removal-confirm cart-popup vibe-popup user-menu-coupon-redeem-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-menu-reward-detail-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cart-popup-close"
                aria-label="Close"
                onClick={() => setRewardDetailReward(null)}
              >
                ×
              </button>
              <h3 id="user-menu-reward-detail-title" className="match-explainer-title user-menu-match-removal-confirm-title">
                {String(rewardDetailReward.title || 'Reward').toUpperCase()}
              </h3>
              <p className="vibe-popup-text">
                Click GET and our Fox Concierge will deliver your reward over email.
              </p>
              <div className="user-menu-reward-detail-actions">
                <button
                  type="button"
                  className="vibe-popup-btn"
                  onClick={() => {
                    const r = rewardDetailReward;
                    setRewardDetailReward(null);
                    if (typeof onRewardConciergeGet === 'function' && r) {
                      void onRewardConciergeGet(r);
                    }
                  }}
                >
                  GET
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {tryNewMaskPoster &&
        createPortal(
          <div
            className="user-menu-match-removal-confirm-overlay"
            role="presentation"
            onClick={() => {
              if (!tryNewMaskRunning) setTryNewMaskPoster(null);
            }}
          >
            <div
              className="user-menu-match-removal-confirm cart-popup vibe-popup user-menu-coupon-redeem-dialog user-menu-try-mask-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-menu-try-mask-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cart-popup-close"
                aria-label="Close"
                onClick={() => setTryNewMaskPoster(null)}
                disabled={tryNewMaskRunning}
              >
                ×
              </button>
              <h3 id="user-menu-try-mask-title" className="match-explainer-title user-menu-match-removal-confirm-title">
                TRY A NEW MASK
              </h3>
              <FoxMaskCutoutLoader
                active={tryNewMaskRunning}
                reserveHintSpace
                className="user-menu-try-mask-loader"
              />
              <p className="vibe-popup-text user-menu-try-mask-prompt">
                If you don&apos;t like how your fox mask sits, we can try a new mask!
              </p>
              <div className="user-menu-try-mask-actions">
                <button
                  type="button"
                  className="vibe-popup-btn user-menu-try-mask-cancel-btn"
                  onClick={() => setTryNewMaskPoster(null)}
                  disabled={tryNewMaskRunning}
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  className="vibe-popup-btn user-menu-try-mask-confirm-btn"
                  onClick={runTryNewMask}
                  disabled={tryNewMaskRunning}
                  aria-busy={tryNewMaskRunning}
                >
                  {tryNewMaskRunning ? 'ADDING...' : 'TRY'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {maskComparison &&
        createPortal(
          <div
            className="user-menu-match-removal-confirm-overlay"
            role="presentation"
            onClick={() => {
              if (!maskUpdating) setMaskComparison(null);
            }}
          >
            <div
              className="user-menu-match-removal-confirm cart-popup vibe-popup user-menu-coupon-redeem-dialog user-menu-mask-compare-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-menu-mask-compare-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cart-popup-close"
                aria-label="Close"
                onClick={() => setMaskComparison(null)}
                disabled={maskUpdating}
              >
                ×
              </button>
              <h3 id="user-menu-mask-compare-title" className="match-explainer-title user-menu-match-removal-confirm-title">
                NEW MASK?
              </h3>
              <div className="user-menu-mask-compare-scroll">
              <div className="user-menu-mask-compare-grid">
                <div className="user-menu-mask-compare-col">
                  <span className="user-menu-mask-compare-label">NEW MASK</span>
                  <img
                    src={maskComparison.newMaskUrl}
                    alt="New fox mask"
                    className="user-menu-mask-compare-img"
                  />
                </div>
                <div className="user-menu-mask-compare-col">
                  <span className="user-menu-mask-compare-label">OLD MASK</span>
                  {maskComparison.oldMaskUrl ? (
                    <img
                      src={maskComparison.oldMaskUrl}
                      alt="Old fox mask"
                      className="user-menu-mask-compare-img"
                    />
                  ) : (
                    <div className="user-menu-mask-compare-img user-menu-mask-compare-img--empty">—</div>
                  )}
                </div>
              </div>
              </div>
              <div className="user-menu-try-mask-actions">
                <button
                  type="button"
                  className="vibe-popup-btn user-menu-try-mask-cancel-btn"
                  onClick={() => setMaskComparison(null)}
                  disabled={maskUpdating}
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  className="vibe-popup-btn user-menu-try-mask-confirm-btn"
                  onClick={confirmMaskUpdate}
                  disabled={maskUpdating}
                  aria-busy={maskUpdating}
                >
                  {maskUpdating ? 'UPDATING…' : 'UPDATE'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <PaidForDateExplainer
        open={showPaidForDateExplainer}
        onClose={() => setShowPaidForDateExplainer(false)}
        onGetPaidForDate={onVibeCheck}
      />

    </div>
  );
}
